/**
 * Overlay the CMS `services` control plane onto the static carousel content.
 *
 * The carousel content (titles, images, biomarker overlays) stays in
 * `data/tests.ts` + `DEFAULT_SERVICES`; the CMS `services` row is the
 * per-country CONTROL PLANE, joined to each card primarily by the CMS
 * `services.path` (which matches the card `path` exactly for every service),
 * falling back to the slug derived from the card path
 * (`slug === path.replace("/tests/", "")`) when a CMS row has no `path`:
 *
 *   - `hidden`                -> exclude the service for that country
 *   - `coming_soon` / `waitlist` -> drive the card's pill from marketStatus
 *   - sort by `(per-country order ?? global order)`
 *
 * The CMS `services` collection is the SOLE driver of the carousel's pill +
 * order — there is no hardcoded control fallback anymore. FAIL-LOUD contract:
 * when the CMS returns no services config (empty array — a CMS failure or an
 * empty collection), the overlay is a no-op so the carousel renders the content
 * cards UNGATED (no pills, stable default order), and the loader
 * (`getServicesConfig` in `lib/cms-server.ts`) fires a Sentry error so the
 * degraded state is never silent. A slug with no CMS row keeps its content card
 * ungated but in its incoming order (sorted after the configured ones).
 */

import type {
  CmsServiceConfig,
  CmsServiceMarketEntry,
  ServiceMarketStatus,
} from "@/lib/cms"
import { isWaitlistOnly, type Country } from "@/i18n/config"
import { gateForPath, isAllowedInCountry } from "@/lib/country-gates"
import type { ServiceCardData } from "@/components/ui/services-carousel"

/** Join key: the carousel `path` (e.g. "/tests/dexa-scan") → service slug. */
export function slugForPath(path: string): string {
  return path.replace("/tests/", "")
}

/**
 * Resolve a CMS service config for a carousel card, matching by the CMS
 * `services.path` first (exact match against the card `path`) and falling back
 * to the slug derived from that path. The path join is the source of truth —
 * it's the only key that's correct for the two services whose CMS `slug` ≠
 * their path-tail (`omega-3-6` @ `/tests/omega-blood-test`,
 * `essential-b-vitamins` @ `/tests/essential-b-vitamins-test`). The slug
 * fallback covers CMS rows that have no `path` set.
 */
function configForCardPath(
  path: string,
  byPath: Map<string, CmsServiceConfig>,
  bySlug: Map<string, CmsServiceConfig>
): CmsServiceConfig | undefined {
  return byPath.get(path) ?? bySlug.get(slugForPath(path))
}

/**
 * Resolve the booking product id (Supabase products id the booking app maps to
 * a Stripe price) for a service from the CMS control plane.
 *
 * The join is by the CMS `services.path` first (exact match against the card /
 * test `path`, e.g. `/tests/omega-blood-test`) and falls back to matching the
 * CMS `slug`. The path join is what makes the two services whose CMS `slug` ≠
 * their path-tail resolve correctly — passing the marketing/canonical slug
 * (`omega-blood-test`) would never match the CMS row whose `slug` is
 * `omega-3-6`, but its `path` does.
 *
 * Pass `pathOrSlug` as the card/test `path` (`/tests/<slug>`) when you have it;
 * a bare slug still works via the slug fallback. Returns the CMS
 * `bookingProductId` when set, else `undefined` so the caller falls back to the
 * hardcoded `PRODUCT_IDS` / `data/tests.ts` mapping. Backward-compatible: an
 * empty config or no matching CMS row yields `undefined`.
 */
export function cmsBookingProductId(
  configs: CmsServiceConfig[],
  pathOrSlug: string
): string | undefined {
  return (
    resolveConfigForPathOrSlug(configs, pathOrSlug)?.bookingProductId ??
    undefined
  )
}

/**
 * Resolve a single CMS service config from a flat config list by the same
 * path-then-slug join the carousel + booking-id lookup use: match the exact CMS
 * `services.path` first (handles the two services whose `slug` ≠ their
 * path-tail), then the slug derived from a `/tests/<slug>` path, then a bare
 * slug. Centralised so every per-service lookup shares one join rule.
 */
function resolveConfigForPathOrSlug(
  configs: CmsServiceConfig[],
  pathOrSlug: string
): CmsServiceConfig | undefined {
  const byPath = configs.find((c) => c.path === pathOrSlug)
  if (byPath) return byPath
  const slug = slugForPath(pathOrSlug)
  return configs.find((c) => c.slug === slug)
}

/**
 * Effective per-country CMS `marketStatus` for a service identified by a card /
 * test `path` (or bare slug). Reuses the shared path-then-slug join +
 * `statusForCountry`, so it defaults to `"available"` when there is no CMS row
 * or no per-country entry. This is the per-service input to the single
 * availability resolver (`resolveServiceAvailability` in
 * `lib/service-availability.ts`); prefer the resolver at call sites — it
 * composes this with the country/category layer.
 */
export function serviceStatusForCountry(
  configs: CmsServiceConfig[],
  pathOrSlug: string,
  country: string
): ServiceMarketStatus {
  const config = resolveConfigForPathOrSlug(configs, pathOrSlug)
  if (!config) return "available"
  return statusForCountry(config, country)
}

/**
 * Whether a service's effective per-country status is `not_available` — the
 * CMS-driven replacement for the hardcoded NL gating (#1758/#1765). Drives the
 * "Not available" pill on imaging add-on cards / the product-info modal and the
 * greyed-out booking-CTA notice on test detail pages. Defaults to available
 * (returns false) when no CMS row or per-country entry exists.
 */
export function isServiceNotAvailable(
  configs: CmsServiceConfig[],
  pathOrSlug: string,
  country: string
): boolean {
  return (
    serviceStatusForCountry(configs, pathOrSlug, country) === "not_available"
  )
}

/** Resolve the effective per-country market entry for a service config. */
function entryForCountry(
  config: CmsServiceConfig,
  country: string
): CmsServiceMarketEntry | undefined {
  const code = country.toLowerCase()
  return config.marketStatus?.find((m) => m.country.toLowerCase() === code)
}

/** Effective status for a service in a country — defaults to "available". */
export function statusForCountry(
  config: CmsServiceConfig,
  country: string
): ServiceMarketStatus {
  return entryForCountry(config, country)?.status ?? "available"
}

/**
 * Effective sort order: per-country override if set, else the global default.
 */
export function orderForCountry(
  config: CmsServiceConfig,
  country: string
): number {
  const entry = entryForCountry(config, country)
  return entry?.order ?? config.order
}

/**
 * Bare `/tests/<slug>` paths whose CMS per-country entry sets `hideFromNav`,
 * for the core navigation (desktop mega menu + mobile slide-out menu).
 *
 * Orthogonal to `marketStatus.status` — a `waitlist` / `coming_soon` service
 * can still render its carousel pill while being pulled out of the menu. The
 * join key is the canonical CMS `path` (`/tests/<slug>`, matching the
 * hardcoded nav links exactly), falling back to `/tests/<slug>` for rows with
 * no `path`. Returns bare paths (no `/{country}/{locale}` prefix) so the nav's
 * `useLocalePath()`-prefixed hrefs are compared against the un-prefixed source.
 *
 * Empty `configs` (CMS failure / empty collection) -> `[]`, so the nav renders
 * ungated. Resilient to the CMS field being absent: a missing `hideFromNav`
 * reads as not-hidden.
 */
export function navHiddenPathsForCountry(
  configs: CmsServiceConfig[],
  country: string
): string[] {
  const code = country.toLowerCase()
  return configs
    .filter((c) =>
      c.marketStatus?.some(
        (m) => m.country.toLowerCase() === code && m.hideFromNav === true
      )
    )
    .map((c) => c.path ?? `/tests/${c.slug}`)
}

/**
 * Apply the CMS services control plane to the carousel items for one country.
 *
 * - drops services whose effective status is `hidden`
 * - tags each surviving card with its effective `marketStatus` so the card
 *   renders the right pill (the carousel reads `card.marketStatus`; there is no
 *   hardcoded pill rule anymore, so a card with no CMS row renders no pill)
 * - sorts by the effective per-country order; cards with no CMS row keep their
 *   incoming relative order AFTER the configured ones (stable)
 *
 * Empty `configs` -> returns `items` unchanged: the carousel renders ungated
 * (no pills, stable default order). The loader fires Sentry in that case
 * (fail-loud), so this stays a pure no-op.
 */
export function applyServicesConfig(
  items: ServiceCardData[],
  configs: CmsServiceConfig[],
  country: string
): ServiceCardData[] {
  // Drop cards whose /tests/<slug> route is country-gated out of this market
  // (registry in lib/country-gates.ts — the same source of truth the sitemap,
  // hreflang, locale picker and search index filter on). Those routes are only
  // SSG'd in the gated countries (`dynamicParams = false`), so a card linking
  // to one elsewhere soft-404s. Runs before the CMS control plane below, which
  // is a no-op when its config is empty (fail-loud contract) and so cannot be
  // relied on to hide a hard-gated route.
  const kept = items.filter((item) => {
    const gate = gateForPath(item.path)
    return gate == null || isAllowedInCountry(gate, country as Country)
  })
  // Preserve the original array identity when nothing is gated out, so the
  // empty-config no-op below still returns the exact input reference.
  if (kept.length !== items.length) items = kept

  if (configs.length === 0) return items

  const bySlug = new Map(configs.map((c) => [c.slug, c]))
  // Path is the primary join key (correct even when slug ≠ path-tail). Only
  // index configs that actually carry a path.
  const byPath = new Map(
    configs.filter((c) => c.path != null).map((c) => [c.path as string, c])
  )

  const waitlist = isWaitlistOnly(country)
  const overlaid = items
    .map((item, index) => {
      const config = configForCardPath(item.path, byPath, bySlug)
      let status = config ? statusForCountry(config, country) : "available"
      // Waitlist markets advertise the imaging line-up as coming later
      // (2026-07-29): an MRI-category card gets the coming-soon pill even
      // without a per-market CMS entry (the flagship full-body MRI may have no
      // CMS row at all — recognized by its canonical path).
      //
      // Only a card the CMS leaves at the `available` default is coerced. An
      // explicit `not_available` is an editor saying "this market cannot
      // deliver this scan", and it keeps its own pill here as it does on the
      // add-on grid and the detail page — coercing it (reverted) turned a
      // deliberate CMS row into a promise the market can't keep.
      if (waitlist) {
        const isImagingCard =
          config?.category === "imaging" || item.path === "/tests/full-body-mri"
        if (isImagingCard && status === "available") {
          status = "coming_soon"
        }
      }
      // `coming_soon` / `waitlist` / `not_available` tag the card so it renders
      // the right pill (the carousel maps `not_available` to the "Not available"
      // ActionBadge, coming_soon/waitlist to the ComingSoonPill); `available`
      // leaves the card untouched (no pill); `hidden` is filtered below.
      // Decorate with order + the original index for a stable sort —
      // unconfigured cards sort last (Infinity) but keep relative order.
      const card: ServiceCardData =
        status === "coming_soon" ||
        status === "waitlist" ||
        status === "not_available"
          ? { ...item, marketStatus: status }
          : item
      return {
        card,
        status,
        index,
        order: config
          ? orderForCountry(config, country)
          : Number.POSITIVE_INFINITY,
      }
    })
    .filter(({ status }) => status !== "hidden")

  return overlaid
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ card }) => card)
}
